import styled, { CSSObject } from 'styled-components';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import FdrWishlistCompareItemFeaturesMobile from './fdr-wishlist-compare-item-features-mobile';
import { WishlistCompareNotSelectedItemInfo } from 'components/domain/wishlist/WishlistCompareNotSelectedItemInfo';
import React from 'react';
import { FdrTripAdvisorInfo } from '@hotelplan/fdr.regular.fusion.fdr-trip-advisor';
import { card } from '@hotelplan/components.common.mixins';
import { BsButton } from '@hotelplan/core.basis.bs-button';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import {
  ProductCardImageWrap,
  ProductTravelType,
  TopOffer,
} from 'components/domain/product-card/ProductCard.styles';
import FdrFlightSectionShort from 'fdr/components/local/fdr-flight-section/fdr-flight-section-short';
import { FdrProductOfferStatus } from '@hotelplan/supergraph-api';
import FdrWishlistItemOverlay, {
  IFdrWishlistItemOverlay,
} from './fdr-wishlist-item-overlay';
import { TFdrWishlistCompareItem } from './fdr-wishlist-compare-items.types';
import { FdrTextBlock } from '@hotelplan/fdr.regular.basis.fdr-text-block';

const columnStyles: CSSObject = {
  flexDirection: 'column',
  width: '50%',
};

export const PriceButtonWrapper = styled(BsButton)(
  sx2CssThemeFn({
    display: 'flex',
    px: 2,
    alignItems: 'center',
    justifyContent: 'space-between',
    '.price-wrap': {
      textAlign: 'left',
    },
    '.price-button-icon': { variant: 'icons.md' },
  })
);

export const PriceWrapper = styled.div(({ theme: { FONT_SIZE } }) =>
  sx2CssThemeFn({
    ...FONT_SIZE.SMALLER,
    '.price': FONT_SIZE.SMALL,
  })
);
export const ColumnWrapper = styled.div<{ i: number; length: number }>(
  ({ i, length }) =>
    sx2CssThemeFn({
      ...columnStyles,
      position: 'relative',
      mr: i === length - 1 ? 0 : 3,
    })
);

export const SectionWrapper = styled.div<{ i: number; length: number }>(
  ({ i, length }) =>
    sx2CssThemeFn({
      display: 'flex',
      ...columnStyles,
      mr: i === length - 1 ? 0 : 3,
    })
);

export const WishlistOrlLinkWrapper = styled.div(({ theme: { colors } }) =>
  sx2CssThemeFn({
    fontSize: 1,
    p: 2,
    textAlign: 'center',
    a: {
      '.icon': {
        verticalAlign: 'middle',
        ml: 2,
        width: '16px',
        height: '16px',
        flexShrink: 0,
      },
      textDecoration: 'underline',
      textDecorationColor: colors.linkUnderlineColor,
      textUnderlineOffset: '6px',
      '&:hover,&:focus': {
        color: colors.defaultText,
        textDecorationColor: colors.defaultText,
      },
    },
  })
);

export const WishlistCompareItemsMobileWrapper = styled.div(
  sx2CssThemeFn(card, {
    position: 'relative',
    py: 3,
    mb: 4,
  })
);

export const ItemsToShowWrapper = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    borderBottom: '1px solid',
    borderColor: 'borderColor',
    pb: 3,
    px: 3,
    mb: 3,
  })
);

export const WishlistCompareNotSelectedItemInfoWrapper = styled(
  WishlistCompareNotSelectedItemInfo
)<{ i: number; length: number }>(({ i, length }) =>
  sx2CssThemeFn({
    ...columnStyles,
    p: 0,
    fontSize: 1,
    h3: { fontSize: 2 },
    a: { p: 2, fontSize: 2 },
    mr: i === length - 1 ? 0 : 3,
  })
);

export const TravelTypeWrapper = styled.div.attrs({
  className: 'travelType',
})(({ theme: { space } }) => ({
  position: 'absolute',
  right: 'auto',
  left: 'auto',
  top: 'auto',
  margin: space[2],
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  '.topOffer .icon': {
    marginRight: 0,
  },
}));

export const WishlistProductTravelType = styled(ProductTravelType)(
  sx2CssThemeFn(({ theme: { radii, space } }) => ({
    borderRadius: radii.default,
    padding: `${space[1]} ${space[2]}`,
    '.icon': {
      marginRight: '3px',
    },
    '.delimiter': {
      px: '3px',
    },
  }))
);

export const TravelTypeIconWrapper = styled.span({
  display: 'flex',
  alignItems: 'center',
  '.travel-type-icon': {
    verticalAlign: 'sub',
  },
});

export const WishlistImageWrapper = styled(ProductCardImageWrap)(
  sx2CssThemeFn({
    height: '110px',
    minHeight: '110px',
    mb: 3,
  })
);

export const WishlistImageLink = styled(FdrRoute)({
  width: '100%',
});

export const SectionHeader = styled.p(({ theme, theme: { space } }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
  marginBottom: space[2],
  '.section-lg-icon': {
    ...theme.icons.lg,
    marginRight: space[2],
  },
  '.section-md-icon': {
    ...theme.icons.md,
    marginRight: space[2],
  },
}));

export const FlightSectionShortWrapper = styled(FdrFlightSectionShort)(
  sx2CssThemeFn({
    mb: 3,
    '.departure-airport, .departure-date, .time-info': {
      fontSize: 2,
    },
    '.airport-logo': {
      height: '100%',
    },
  }),
  {
    '.no-mb': {
      marginBottom: 0,
    },
  }
);

export const ItemsToShowNoBorderWrapper = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    px: 3,
    pb: 3,
  })
);

export const RoomsWrapper = styled.div(
  sx2CssThemeFn({
    mb: 2,
    '.wishlist-room': {
      mb: 2,
      color: 'secondary',
    },
    '.children-rooms': {
      color: 'secondary',
    },
  })
);

export const TopOfferWrapper = styled(TopOffer)(
  ({ theme: { space, radii } }) => ({
    borderRadius: radii.default,
    left: space[2],
    position: 'relative',
    right: 'auto',
    padding: `${space[1]} ${space[2]}`,
  })
);

export const WishlistCompareItemFeaturesMobileWrapper = styled(
  FdrWishlistCompareItemFeaturesMobile
)<{ items: TFdrWishlistCompareItem[] }>(({ items }) =>
  sx2CssThemeFn({
    borderBottom: '1px solid',
    borderColor: 'borderColor',
    pb: 3,
    px: 3,
    mb: 3,
    flexDirection: 'column',
    '.featureName, .featureGroupName': {
      zIndex: items.some(it => it?.status === FdrProductOfferStatus.Available)
        ? 3
        : 0,
    },
  })
);

export const TextBlockWrapper = styled(FdrTextBlock)(
  ({ theme: { space } }) => ({
    h1: {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 'bold',
      marginBottom: space[2],
    },
    ul: {
      listStyle: 'disc',
      paddingLeft: space[3],
    },
  })
);

export const IncludedInPriceHeader = styled.h1(
  sx2CssThemeFn({
    mb: 2,
    fontSize: 2,
    '.icon': {
      mr: 2,
      width: '16px',
      height: '16px',
    },
  })
);

export const TripAdvisorInfoWrapper = styled(FdrTripAdvisorInfo)(
  sx2CssThemeFn({
    '.tripAdvisorInfoText': {
      fontSize: 2,
      pt: '12px',
    },
    '.icon': {
      variant: 'icons.sm',
    },
    '.tripAdvisorInfoNumber': {
      fontWeight: 'bold',
    },
    '.tripadvisorLogo': {
      height: '25px',
      width: '29px',
    },
    mr: 2,
    flex: '1 1 0%',
  })
);

export const ItemsToShowPriceButtonWrapper = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    px: 3,
  })
);

export const TransferInfoWrapper = styled(SectionWrapper)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export const WishlistItemOverlayWrapper = styled(FdrWishlistItemOverlay)<{
  index: number;
}>(({ index }) =>
  sx2CssThemeFn({
    left: index === 0 ? 0 : '50%',
    right: index === 0 ? '50%' : 0,
    '.flights': {
      flexDirection: 'column',
    },
  })
) as React.FC<IFdrWishlistItemOverlay & { index: number }>;
